import * as React from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';

import { IBasicTableColumn, IAdvanceTableToolbarProps, TableListingPage, notification } from 'ui-lib';
import { OrganisationSelect } from '../../components/organisation-select';

import { useAccountStore, useRoutesStore, useUserStore } from '../../../../store';
import { deleteUser } from '../utils';
import { buildNavigatePath, hasPermission } from '../../utils';
import { USER_ROUTE_CODES } from './';
import { useSearchableTableStates } from '../../components/table-utils';
import { IDisplayUser } from 'store/user-store';
import { getErrorMessage } from 'common-utils';
import userCss from './user.css';
import { ADMIN_PERMISSIONS, usePermissionContext } from 'contexts/permissions/permission-provider';

export const ManageUsersComponent = observer(() => {
    const { hasPermission } = usePermissionContext();
    const modifyPermission = hasPermission(ADMIN_PERMISSIONS.USERS_MODIFY);
    const accountStore = useAccountStore();
    const userStore = useUserStore();
    const navigate = useNavigate();

    const routeStore = useRoutesStore();
    const addRoute = routeStore.getRoute(USER_ROUTE_CODES.ADD);
    const editRoute = routeStore.getRoute(USER_ROUTE_CODES.EDIT);

    const { onFilter, filteredDataSource } = useSearchableTableStates(userStore.displayUsers, ['name', 'email']);

    const [selectedOrganisationKey, setSelectedOrganisationKey] = React.useState<number>(undefined);

    const filteredUser = React.useMemo<IDisplayUser[]>(() => {
        if (!selectedOrganisationKey) return filteredDataSource;
        return filteredDataSource.filter((user) => {
            if (!user.account) return false;
            return user.account.id === selectedOrganisationKey;
        });
    }, [filteredDataSource, selectedOrganisationKey]);

    const onAdd = () =>
        navigate(addRoute.path.concat(!selectedOrganisationKey ? '' : `?org=${selectedOrganisationKey}`));
    const onSelectedOrganisationChange = (id: number) => setSelectedOrganisationKey(id);
    const onDelete = async (id: string) => {
        try {
            await userStore.deleteUser(id);
        } catch (err) {
            notification.error({
                message: 'Delete user error',
                description: getErrorMessage(err),
            });
        }
    };

    const columns: IBasicTableColumn[] = [
        {
            code: 'name',
            title: 'User',
            dataIndex: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
            actions: modifyPermission
                ? [
                      {
                          key: 'id',
                          onClick: (id: number) => navigate(buildNavigatePath(editRoute.path, { ':id': `${id}` })),
                      },
                  ]
                : undefined,
        },
        {
            code: 'email',
            title: 'Email',
            dataIndex: 'email',
        },
        {
            code: 'organisation',
            title: 'Organisation',
            dataIndex: 'organisation',
        },
        {
            code: 'userRoles',
            title: 'User role',
            dataIndex: 'userRoles',
        },
        ...(modifyPermission
            ? [
                  {
                      code: 'action',
                      title: 'Action',
                      dataIndex: 'action',
                      actions: [
                          {
                              key: 'id',
                              label: 'Delete',
                              onClick: (id: string) => deleteUser(id, onDelete),
                          },
                      ],
                  },
              ]
            : []),
    ];

    return (
        <TableListingPage
            title=''
            tableProps={{
                title: 'All users',
                loadingState: userStore.dataLoading,
                table: {
                    columns: columns,
                    dataSource: filteredUser,
                },
                toolbar: {
                    searchPlaceholder: 'Search users',
                    onSearch: onFilter,
                    onAdd: modifyPermission ? onAdd : undefined,
                    onReload: () => userStore.loadUsers(true),
                    extraItems: [
                        <OrganisationSelect
                            key='organisation'
                            placeholder='Organisation'
                            allowClear={true}
                            expandAll={true}
                            treeDataSource={accountStore.treeDataSource}
                            onChange={onSelectedOrganisationChange}
                            className={userCss.extraSpacing}
                        />,
                    ],
                } as IAdvanceTableToolbarProps,
            }}
        />
    );
});
